import { cn } from '@/lib/utils';
import type { CandidateSkillDetail, SkillSource } from '@/types/candidates';
import { CheckCircle2, X } from 'lucide-react';

const SOURCE_LABELS: Record<Exclude<SkillSource, 'MANUAL'>, string> = {
  LINKEDIN: 'LinkedIn',
  CV_PARSED: 'CV',
  VACANCY_SUGGESTION: 'Vacancy',
};

interface SkillChipProps {
  skill: CandidateSkillDetail;
  canEdit: boolean;
  onToggle: () => void;
  onRemove: () => void;
}

export function SkillChip({ skill, canEdit, onToggle, onRemove }: SkillChipProps) {
  const sourceLabel =
    skill.source !== 'MANUAL' ? SOURCE_LABELS[skill.source] : null;

  return (
    <li
      className={cn(
        'inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-xs transition-colors',
        skill.verified
          ? 'border-hygge/40 bg-hygge/15 text-hygge'
          : 'border-border bg-transparent text-muted-foreground'
      )}
    >
      <button
        type="button"
        onClick={canEdit ? onToggle : undefined}
        disabled={!canEdit}
        className={cn(
          'inline-flex items-center gap-1.5 text-left',
          canEdit && 'cursor-pointer hover:brightness-95',
          !canEdit && 'cursor-default'
        )}
        aria-label={skill.verified ? `Unverify ${skill.name}` : `Verify ${skill.name}`}
      >
        {skill.verified ? <CheckCircle2 className="h-3 w-3 shrink-0" /> : null}
        <span className="max-w-[200px] truncate" title={skill.name}>
          {skill.name}
        </span>
        {sourceLabel ? (
          <span className="text-[10px] uppercase tracking-wide opacity-70">
            {sourceLabel}
          </span>
        ) : null}
      </button>
      {canEdit ? (
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            onRemove();
          }}
          className="ml-0.5 rounded-full p-0.5 transition-colors hover:bg-foreground/10"
          aria-label={`Remove ${skill.name}`}
        >
          <X className="h-3 w-3" />
        </button>
      ) : null}
    </li>
  );
}